import Controller, { OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ActionAPI, FieldAPI, FormAPI, ModalProps, RadioGroupAPI } from '@philips-internal/oswald-ui-components/lib/components';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { ModalService, SelectableItem, ToastService } from '@philips-internal/oswald-ui-components';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { Value } from '@philips-internal/oswald-ui-core';

import { Inject } from '@odinjs/odin';
import moment from 'moment';

import { DATE_PATTERN, FULL_LOCALE_DATE, MONTH_YEAR_FORMAT } from '../../../../constants/date-utils';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { HealthHistoryMedicinesViewerAliases as Alias, FragmentNames } from '../../../../codegen';
import BaseController from '../../../BaseController';
import { HealthHistoryListContext } from '../../service/health-history-list-service';
import { DateTypeEntry, DateTypeId, DomainType, HealthHistoryMedicineResponse, HealthHistoryType } from '../../health-history-types';
import { Endpoint } from '../../../../constants/endpoints';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { FunctionName, PEP_281Parameters } from '../../../../utils/parameters';
import DialogService from '../../../../services/dialog-service';
import { MedicinesRadioGroupId } from '../radio-group-data/radio-group-data-expressions';
import { ApplicationRoute } from '../../../../constants/routes';
import { HealthProfessionalUser } from '../../../../app/authentication';
import HealthHistoryCommons from '../../health-history-commons';

enum VerifyReviewParameter {
  ALLOWED_REVIEW      = 'S',
  NOT_ALLOWED_REVIEW  = 'N'
}

@Controller({ fragment: FragmentNames.HEALTH_HISTORY_MEDICINES_VIEWER })
export default class HealthHistoryMedicinesViewerController extends BaseController {

  @Inject(Alias.ACTION_INACTIVATE_HEALTH_HISTORY_MEDICINES)
  private actionInactivateAPI!: ActionAPI

  @Inject(Alias.ACTION_NEW_HEALTH_HISTORY_MEDICINES)
  private actionNewAPI!: ActionAPI;

  @Inject(Alias.ACTION_REVIEW_HEALTH_HISTORY_MEDICINES)
  private actionReviewAPI!: ActionAPI;

  @Inject(Alias.FORM_HEALTH_HISTORY_MEDICINES_VIEWER)
  private formAPI!: FormAPI;

  @Inject(Alias.FIELD_MEDICATION)
  private fieldMedicationAPI!: FieldAPI;

  @Inject(Alias.FIELD_UNREGISTERED_MEDICINE)
  private fieldUnregistredMedAPI!: FieldAPI;

  @Inject(Alias.FIELD_QT_DOSE)
  private fieldQtDoseAPI!: FieldAPI;

  @Inject(Alias.FIELD_UNITY_MEASURE)
  private fieldUnityMeasureAPI!: FieldAPI;

  @Inject(Alias.FIELD_ADM_CHANNEL)
  private fieldAdmChannelAPI!: FieldAPI;

  @Inject(Alias.FIELD_INTERVAL)
  private fieldIntervalAPI!: FieldAPI;

  @Inject(Alias.FIELD_SCHEDULES)
  private fieldSchedulesAPI!: FieldAPI;

  @Inject(Alias.FIELD_SECURITY_LEVEL)
  private fieldSecurityLvlAPI!: FieldAPI;

  @Inject(Alias.FIELD_PROVENANCE)
  private fieldProvenanceAPI!: FieldAPI;

  @Inject(Alias.FIELD_INTERRUPT_REASON)
  private fieldInterruptReasonAPI!: FieldAPI;

  @Inject(Alias.FIELD_DATE_TYPE)
  private fieldDateTypeAPI!: FieldAPI;

  @Inject(Alias.FIELD_DATE_START)
  private fieldDateStartAPI!: FieldAPI;

  @Inject(Alias.FIELD_DATE_END)
  private fieldDateEndAPI!: FieldAPI;

  @Inject(Alias.FIELD_DATE_LAST_DOSE)
  private fieldDateLastDoseAPI!: FieldAPI;

  @Inject(Alias.FIELD_REACTION)
  private fieldReactionAPI!: FieldAPI;

  @Inject(Alias.FIELD_OBSERVATION)
  private fieldObservationAPI!: FieldAPI;

  @Inject(Alias.FIELD_REVIEW_DATE)
  private fieldReviewDateAPI!: FieldAPI;

  @Inject(Alias.FIELD_REVIEW_USERNAME)
  private fieldReviewUserAPI!: FieldAPI;

  @Inject(Alias.FIELD_FIRST_TRIMESTER)
  private fieldFirstTrimesterAPI!: FieldAPI;

  @Inject(Alias.FIELD_SECOND_TRIMESTER)
  private fieldSecondTrimesterAPI!: FieldAPI;

  @Inject(Alias.FIELD_THIRD_TRIMESTER)
  private fieldThirdTrimesterAPI!: FieldAPI;

  @Inject(Alias.FIELD_JUSTIFICATION_FOR_END)
  private fieldJustificationForEndAPI!: FieldAPI;

  @OnMount({ target: Alias.LAYOUT_HEALTH_HISTORY_MEDICINES })
  handleMountLayout(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    InformationBarUtils.collapsedBar = true;
    this.context.set({encounterId, patientId});
    HealthHistoryListContext.currentTab = HealthHistoryType.MEDICINES;
    this.getPatientInfo();
  }

  @OnMount({ target: Alias.FORM_HEALTH_HISTORY_MEDICINES_VIEWER })
  handleMountForm(): void {
    this.formAPI.setLoading(true);
    this.handleJustificationForEnd();
    this.handleFetchMedicine();
  }

  @OnMount({ target: Alias.RADIO_GROUP_CONDITION })
  handleMountRadioGroup(event: MountEvent): void {
    const radioGroup = event.target as RadioGroupAPI;
    this.setRadioGroupValues(MedicinesRadioGroupId.CONTINUOUS_MEDICATION, radioGroup)
  }

  @OnUnmount({ target: Alias.LAYOUT_HEALTH_HISTORY_MEDICINES })
  handleUnmount(): void {
    HealthHistoryListContext.clean();
    HealthHistoryListContext.currentTab = HealthHistoryType.MEDICINES;
  }

  @OnClick({ target: Alias.ACTION_INACTIVATE_HEALTH_HISTORY_MEDICINES })
  handleClickInactivate(): void {
    this.openModalInactivation();
  }

  @OnClick({ target: Alias.ACTION_REVIEW_HEALTH_HISTORY_MEDICINES })
  handleClickReview(): void {
    this.handleDialogReview();
  }

  @OnClick({ target: Alias.ACTION_NEW_HEALTH_HISTORY_MEDICINES })
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.HEALTH_HISTORY_MEDICINES_CREATE, {
      encounterId,
      patientId,
    });
  }

  public getPatientInfo(): void {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PATIENT_GENDER, {encounterId})
      .then((res) => {
        const data = res.body as Record<string, Value>;
        this.displayPregnancyFields(data?.gender);
      }).catch((err) => err?.body?.errorMessage && this.showToast(err.body.errorMessage, ToastLevel.ERROR));
  }

  private displayPregnancyFields(gender: any): void {
    const genderFemale = 'F';
    HealthHistoryCommons.setVisibleFields([
      this.fieldFirstTrimesterAPI,
      this.fieldSecondTrimesterAPI,
      this.fieldThirdTrimesterAPI], gender && gender === genderFemale);
  }

  public handleJustificationForEnd(): void {
    const justificationForEnd = FoundationUtils.findModuleParam(FunctionName.PEP_281, PEP_281Parameters.JUSTIFICATION_FOR_END)
    HealthHistoryCommons.setVisibleField(this.fieldJustificationForEndAPI, BooleanUtils.isTrueValue(justificationForEnd?.toString()));
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'health-history-medicines-viewer',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private handleDateTypeFields(valueDomain: string): void {
    let entry = DateTypeEntry.DAY_MONTH_YEAR;

    switch (valueDomain) {
      case DateTypeId.MONTH_YEAR:
        entry = DateTypeEntry.MONTH_YEAR;
        break;
      case DateTypeId.YEAR:
        entry = DateTypeEntry.YEAR;
        break;
    }

    this.fieldDateStartAPI.setEntry(entry);
  }

  private getParamAccess(module: string, parameter: string): string {
    return FoundationUtils.findModuleParam(module, parameter) as string;
  }

  private enableReview(): void {
    const isEnabledReview = this.getParamAccess(FunctionName.PEP_281, PEP_281Parameters.ALLOW_REVIEW_HEALTH_HISTORY);
    const allowInfo = isEnabledReview === VerifyReviewParameter.ALLOWED_REVIEW && InformationBarUtils.allowInfo && HealthHistoryListContext.isAllowEdit;
    this.actionReviewAPI.setEnabled(allowInfo);
  }

  private handleDatesToShow(record: HealthHistoryMedicineResponse): string | undefined {
    const beginningDate  = record.beginningDate as DomainType;
    const isDayMonthYear = beginningDate.valueDomain === DateTypeId.DAY_MONTH_YEAR;
    const isMonthYear    = beginningDate.valueDomain === DateTypeId.MONTH_YEAR;

    if (isDayMonthYear) {
      return record.startDate ? moment(String(record.startDate)).utc().format(DATE_PATTERN) : undefined;
    }

    if (isMonthYear) {
      return record.startMonth ? moment(String(record.startMonth)).utc().format(MONTH_YEAR_FORMAT) : undefined;
    }

    return record.startYear as string | undefined;
  }

  private handleData(record: HealthHistoryMedicineResponse): void {
    const alertDescription        = BooleanUtils.isTrueValue(record.alertDescription);
    const firstTrimester          = BooleanUtils.isTrueValue(record.firstTrimester);
    const secondTrimester         = BooleanUtils.isTrueValue(record.secondTrimester);
    const selfMedication          = BooleanUtils.isTrueValue(record.selfMedication);
    const thirdTrimester          = BooleanUtils.isTrueValue(record.thirdTrimester);
    const unknownEnd              = BooleanUtils.isTrueValue(record.unknownEnd);
    const unknownInfoMedication   = BooleanUtils.isTrueValue(record.unknownInfoMedication);
    const unknownLastDose         = BooleanUtils.isTrueValue(record.unknownLastDose);
    const unknownStart            = BooleanUtils.isTrueValue(record.unknownStart);

    const recordDate              = moment(record.recordDate).local().format(FULL_LOCALE_DATE);
    const startDate               = this.handleDatesToShow(record);
    const endDate                 = moment(record.endDate).utc().format(DATE_PATTERN);
    const dateLastDose            = moment(record.dateLastDose).utc().format(DATE_PATTERN);
    const reviewDate              = moment(record.reviewDate).local().format(FULL_LOCALE_DATE);

    const beginningDate           = record.beginningDate?.description as string;
    const interruptReason         = record.interruptReason?.description as string;
    const interval                = record.interval?.description as string;
    const provenance              = record.provenance?.description as string;
    const unityMeasurement        = record.unityMeasurement?.description as string;
    const professionalName        = record.professional?.description as string;
    const medicationDescription   = record.medicationDescription?.description as string;
    const administrationChannel   = record.administrationChannel?.description as string;
    const securityLevel           = record.securityLevel?.description;
    const radioData               = record.continuousMedication;
    const justificationForEnd     = record.justificationForEnd?.description as string;
    const continuousMedication    = { key: radioData.id, label: '', value: radioData.id};

    const medicinesRecord = {
      ...record,
      administrationChannel,
      alertDescription,
      beginningDate,
      continuousMedication,
      dateLastDose,
      endDate,
      firstTrimester,
      interruptReason,
      interval,
      justificationForEnd,
      medicationDescription,
      professionalName,
      provenance,
      recordDate,
      reviewDate,
      secondTrimester,
      securityLevel,
      selfMedication,
      startDate,
      thirdTrimester,
      unityMeasurement,
      unknownEnd,
      unknownInfoMedication,
      unknownLastDose,
      unknownStart,
    }

    this.context.set({ medicines: medicinesRecord });
    this.handleDateTypeFields(beginningDate);
    this.formAPI.setEditing(true);
    this.formAPI.validate();
    this.formAPI.setDisabled(true);
    this.formAPI.setData({ ...medicinesRecord });
    this.handleVisibleFields(record);
  }

  private handleVisibleFields(record: HealthHistoryMedicineResponse): void {
    this.fieldMedicationAPI.setVisible(BooleanUtils.isNotNull(record.medicationDescription?.description));
    this.fieldUnregistredMedAPI.setVisible(BooleanUtils.isNotNull(record.unregisteredMedication));
    this.fieldQtDoseAPI.setVisible(BooleanUtils.isNotNull(record.qtDose));
    this.fieldUnityMeasureAPI.setVisible(BooleanUtils.isNotNull(record.unityMeasurement?.description));
    this.fieldAdmChannelAPI.setVisible(BooleanUtils.isNotNull(record.administrationChannel?.description));
    this.fieldIntervalAPI.setVisible(BooleanUtils.isNotNull(record.interval?.description));
    this.fieldSchedulesAPI.setVisible(BooleanUtils.isNotNull(record.schedules));
    this.fieldSecurityLvlAPI.setVisible(BooleanUtils.isNotNull(record.securityLevel?.description));
    this.fieldProvenanceAPI.setVisible(BooleanUtils.isNotNull(record.provenance?.description));
    this.fieldInterruptReasonAPI.setVisible(BooleanUtils.isNotNull(record.interruptReason?.description));
    this.fieldDateTypeAPI.setVisible(BooleanUtils.isNotNull(record.beginningDate?.description));
    this.fieldDateStartAPI.setVisible(BooleanUtils.isNotNull(record.startDate));
    this.fieldDateEndAPI.setVisible(BooleanUtils.isNotNull(record.endDate));
    this.fieldDateLastDoseAPI.setVisible(BooleanUtils.isNotNull(record.dateLastDose));
    this.fieldReactionAPI.setVisible(BooleanUtils.isNotNull(record.reaction));
    this.fieldObservationAPI.setVisible(BooleanUtils.isNotNull(record.observation));
    this.fieldReviewDateAPI.setVisible(BooleanUtils.isNotNull(record.reviewDate));
    this.fieldReviewUserAPI.setVisible(BooleanUtils.isNotNull(record.reviewUsername));
    this.fieldJustificationForEndAPI.setVisible(BooleanUtils.isNotNull(record.justificationForEnd?.description));
  }

  private handleFetchMedicine(): Promise<void> {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    const allowInfo = InformationBarUtils.allowInfo && HealthHistoryListContext.isAllowEdit;

    return HttpService.get(Endpoint.GET_HEALTH_HISTORY_MEDICINE, { sequenceNumberMedicine: idHealthHistory })
      .then(response => {
        const record = response.body as HealthHistoryMedicineResponse;
        const user = foundationContext.getUser() as HealthProfessionalUser;
        const showInactivate = record.professional.id === user.physicalPersonId && Boolean(record.showInactivate) && allowInfo;

        this.handleData(record);
        this.actionInactivateAPI.setEnabled(showInactivate);

        this.context.set({
          alertDescription:       record.alertDescription,
          firstTrimester:         record.firstTrimester,
          secondTrimester:        record.secondTrimester,
          selfMedication:         record.selfMedication,
          thirdTrimester:         record.thirdTrimester,
          unknownEnd:             record.unknownEnd,
          unknownInfoMedication:  record.unknownInfoMedication,
          unknownLastDose:        record.unknownLastDose,
          unknownStart:           record.unknownStart,
        });

      }).catch((err) => err?.body?.errorMessage && this.showToast(err?.body, ToastLevel.ERROR))
      .finally(() => {
        this.formAPI.setLoading(false);
        this.actionNewAPI.setEnabled(allowInfo);
        this.enableReview();
      })
  }

  private setRadioGroupValues(expressionId: number, radioGroup: RadioGroupAPI): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PAIN_SCALE_DATA, { encounterId, expressionId, patientId })
      .then(response => {
        const responseBody = response.body as SelectableItem[];
        radioGroup.setData(responseBody);
      }).catch((error) => {
        error?.body?.errorMessage && this.showToast(error.body.errorMessage, ToastLevel.ERROR)
      });
  }

  private openModalInactivation(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams() as Record<string, Value>;
    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'health-history-inactivate',
      id: 'health-history-inactivate',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'title.disable-record',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      idHealthHistory,
    } as Record<string, Value>;

    ModalService.openDefault(modalProperties);
  }

  private handleReviewMedicines(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams() as Record<string, Value>;
    this.formAPI.setLoading(true);

    const body = {
      idHealthHistory
    } as Record<string, Value>;

    HttpService.put(Endpoint.REVIEW_HEALTH_HISTORY_MEDICINES, undefined, {body})
      .then(() => {
        this.historyService.back();
        this.showToast('text.successfully-reviewed', ToastLevel.SUCCESS);
      }).catch(err => err?.body && this.showToast(err?.body, ToastLevel.ERROR))
      .finally(() => this.formAPI.setLoading(false));
  }

  private handleDialogReview(): void {
    DialogService.open({
      descriptionDialog: '',
      onClickCancel: () => { applicationContext.popNavigationItem()},
      onClickOk: () => { this.handleReviewMedicines()},
      titleDialog: 'text.confirm-review-item',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'text.review-item-hh',
    });
  }
}
